import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// Helper function to get auth headers
const getAuthHeaders = (token) => ({
  headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
  withCredentials: true
});

// Async thunks
export const fetchPages = createAsyncThunk(
  'pages/fetchPages',
  async ({ token, params = {} }) => {
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(`/pages?${queryParams}`, getAuthHeaders(token));
    return response.data;
  }
);

export const fetchPageById = createAsyncThunk(
  'pages/fetchPageById',
  async ({ pageId, token }) => {
    const response = await axios.get(`/pages/${pageId}`, getAuthHeaders(token));
    return response.data;
  }
);

export const fetchPageBySlug = createAsyncThunk(
  'pages/fetchPageBySlug',
  async (slug) => {
    const response = await axios.get(`/pages/slug/${slug}`);
    return response.data;
  }
);

export const createPage = createAsyncThunk(
  'pages/createPage',
  async ({ pageData, token }) => {
    const response = await axios.post(`/pages`, pageData, getAuthHeaders(token));
    return response.data;
  }
);

export const updatePage = createAsyncThunk(
  'pages/updatePage',
  async ({ pageId, pageData, token }) => {
    const response = await axios.put(`/pages/${pageId}`, pageData, getAuthHeaders(token));
    return response.data;
  }
);

export const deletePage = createAsyncThunk(
  'pages/deletePage',
  async ({ pageId, token }) => {
    await axios.delete(`/pages/${pageId}`, getAuthHeaders(token));
    return pageId;
  }
);

export const duplicatePage = createAsyncThunk(
  'pages/duplicatePage',
  async ({ pageId, token }) => {
    const response = await axios.post(`/pages/${pageId}/duplicate`, {}, getAuthHeaders(token));
    return response.data;
  }
);

const initialState = {
  pages: [],
  currentPage: null,
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pages: 1,
    total: 0
  }
};

const pageSlice = createSlice({
  name: 'pages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPage: (state) => {
      state.currentPage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch pages
      .addCase(fetchPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPages.fulfilled, (state, action) => {
        state.loading = false;
        state.pages = action.payload.pages;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch page by ID
      .addCase(fetchPageById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchPageById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch page by slug
      .addCase(fetchPageBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchPageBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Create page
      .addCase(createPage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.loading = false;
        state.pages = [action.payload.page, ...state.pages];
      })
      .addCase(createPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update page
      .addCase(updatePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.pages.findIndex(
          page => page._id === action.payload.page._id
        );
        if (index !== -1) {
          state.pages[index] = action.payload.page;
        }
        if (state.currentPage && state.currentPage._id === action.payload.page._id) {
          state.currentPage = action.payload.page;
        }
      })
      .addCase(updatePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Delete page
      .addCase(deletePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.loading = false;
        state.pages = state.pages.filter(page => page._id !== action.payload);
        if (state.currentPage && state.currentPage._id === action.payload) {
          state.currentPage = null;
        }
      })
      .addCase(deletePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Duplicate page
      .addCase(duplicatePage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicatePage.fulfilled, (state, action) => {
        state.loading = false;
        state.pages = [action.payload.page, ...state.pages];
      })
      .addCase(duplicatePage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { clearError, clearCurrentPage } = pageSlice.actions;
export default pageSlice.reducer;
